import { getContactFullName, getPrimaryPhone } from 'app/pages/crm/shared/crm.utils';
import { dealApi, loanApplicationsApi, loanApplicationsProgressApi } from 'app/services/api';
import _ from 'lodash';

import { getMilestoneCompletedDate } from 'utils/milestones';
import { getPhoneDigits } from 'utils/phone';

const getLoanSummary = async applicationId => {
  try {
    const { data } = await loanApplicationsApi.getLoanSummary({ applicationId });

    return data ?? null;
  } catch {
    return null;
  }
};

const getApplicationStatus = async applicationId => {
  try {
    const { data } = await dealApi.getDataByApplicationId({ applicationId });

    return data?.applicationStatus ?? null;
  } catch {
    return null;
  }
};

const getCurrentMilestone = async applicationId => {
  try {
    const { data } = await loanApplicationsProgressApi.getApplicationProgress({ applicationId });

    return data?.lastCompleted ?? null;
  } catch {
    return null;
  }
};

// LoanPreviewSummary.loanAmount is a Money value: serialized as a plain number in
// this app, but tolerate an object shape ({ amount }) defensively.
const toAmount = value => {
  if (_.isObject(value)) {
    return value.amount ?? null;
  }

  return value ?? null;
};

export const enrichLinkedApplications = async applicationLinks => {
  if (!applicationLinks?.length) {
    return applicationLinks ?? [];
  }

  return Promise.all(
    applicationLinks.map(async link => {
      const { applicationId } = link;

      if (!applicationId) {
        return link;
      }

      const [summary, milestone, applicationStatus] = await Promise.all([
        getLoanSummary(applicationId),
        getCurrentMilestone(applicationId),
        getApplicationStatus(applicationId),
      ]);

      const loanNumber = summary?.loanNumber ?? link.loanNumber ?? null;

      return {
        ...link,
        loanNumber,
        applicationNumber: loanNumber,
        loanPurpose: summary?.loanPurpose ?? link.loanPurpose ?? null,
        loanAmount: toAmount(summary?.loanAmount),
        milestoneName: milestone?.name ?? null,
        milestoneColor: milestone?.color ?? null,
        isTermination: !!milestone?.isTermination,
        completedDate: getMilestoneCompletedDate(milestone),
        applicationStatus: applicationStatus ?? link.applicationStatus ?? null,
      };
    }),
  );
};

const unpagedContactsResponseWarning = { hasFired: false };

/**
 * One-time console notice that GET /api/contacts answered a paged request with the pre-paging
 * bare array, so the response was paged and filtered client-side instead.
 * TODO(DEV-6120): delete this helper, its console notice and pageContactsClientSide once
 * Lender.Optimus.CRM#125 is deployed everywhere this UI runs.
 * @param {boolean} isUnpagedResponseToPagedRequest
 */
export const warnOnceOnUnpagedContactsResponse = isUnpagedResponseToPagedRequest => {
  if (!isUnpagedResponseToPagedRequest || unpagedContactsResponseWarning.hasFired) {
    return;
  }

  unpagedContactsResponseWarning.hasFired = true;

  console.error(
    '[crm] GET /api/contacts returned an unpaged array for a paged request — paging, search and the lifecycle filter were applied client-side. Deploy Lender.Optimus.CRM#125.',
  );
};

// Below this many digits a term is not treated as a phone at all — a two-digit run matches almost
// every number. Mirrors ContactMatchKeys.MinPhoneDigits in Lender.Optimus.CRM.
const MIN_PHONE_SEARCH_DIGITS = 3;
const US_COUNTRY_CODED_DIGITS = 11;

const getContactPrimaryPhone = contact => getPrimaryPhone(contact.phones) || contact.primaryPhone;

// No phone and no company name here — matched on normalized digit runs below, and never matched at
// all, respectively. The server predicate (ContactsRepository.BuildSearchPredicate) substring-matches
// FirstName/LastName, PrimaryEmail and every Emails row — no phone, no company name — so a haystack
// that included either would match rows the API never returns, and those searches would silently
// stop working the day the paged endpoint takes over.
const getContactSearchHaystack = contact =>
  [
    getContactFullName(contact),
    ...(contact.emails ?? []).map(({ emailAddress }) => emailAddress),
    contact.primaryEmail,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

// ContactMatchKeys.Phone: digits with a US country code dropped, so "+1 (512) 555-0101" and
// "5125550101" reach the same stored digit run. A non-US number keeps its country code rather than
// losing a significant leading digit.
const getPhoneSearchDigits = term => {
  const digits = getPhoneDigits(term);

  return digits.length === US_COUNTRY_CODED_DIGITS && digits.startsWith('1')
    ? digits.slice(1)
    : digits;
};

// Every stored phone, not only the primary one — the server predicate matches x.PrimaryPhone OR any
// x.Phones row, so a fallback that read one of them would answer a narrower set than the API it
// stands in for.
const getContactPhoneDigitRuns = contact =>
  [getContactPrimaryPhone(contact), ...(contact.phones ?? []).map(({ phoneNumber }) => phoneNumber)]
    .filter(Boolean)
    .map(getPhoneDigits);

// The grid renders the phone through formatPhoneNumberForDisplay, so a user searching by the number
// they can see ("(314) 400-0314", "314-400") types punctuation the stored value does not carry.
// Comparing digit runs makes both spellings hit the same contact — the same normalization
// Lender.Optimus.CRM applies server-side in ContactsRepository.BuildSearchPredicate.
const matchesContactSearch = (contact, search) => {
  const term = search?.trim();

  if (!term) {
    return true;
  }

  const termDigits = getPhoneSearchDigits(term);
  const matchesPhone =
    termDigits.length >= MIN_PHONE_SEARCH_DIGITS &&
    getContactPhoneDigitRuns(contact).some(digits => digits.includes(termDigits));

  return getContactSearchHaystack(contact).includes(term.toLowerCase()) || matchesPhone;
};

const matchesLifecycleStage = (contact, lifecycleStage) =>
  !lifecycleStage || contact.lifecycleStage === lifecycleStage;

/**
 * Applies the search, lifecycle filter and page window a paged request asked for to the full set
 * an older CRM returns, so the list stays usable against an API that ignores them — without it
 * the pager renders one page holding every contact and every search returns the same rows.
 * @param {Array} contacts the whole unfiltered set the endpoint answered with
 * @param {Object} params the paged request that was sent
 * @param {string} [params.search]
 * @param {string} [params.lifecycleStage]
 * @param {number} params.page 1-based page the endpoint was asked for
 * @param {number} [params.pageSize]
 * @returns {{ contacts: Array, totalCount: number }}
 */
export const pageContactsClientSide = (contacts, { search, lifecycleStage, page, pageSize }) => {
  const matched = contacts.filter(
    contact =>
      matchesContactSearch(contact, search) && matchesLifecycleStage(contact, lifecycleStage),
  );
  const startIndex = Math.max(page - 1, 0) * (pageSize || 0);

  return {
    contacts: pageSize ? matched.slice(startIndex, startIndex + pageSize) : matched,
    totalCount: matched.length,
  };
};
